/* ───────────────────────────────────────────────────────────
   Dialogue, Hero Collection PDP renderer + interactions
   Renders any of the three SKUs into #app and wires up the
   gallery, qty stepper, sticky add-to-bag, accordions,
   reveal-on-scroll, and sign routing.
   ─────────────────────────────────────────────────────────── */
(function () {
  const P = window.DIALOGUE_PRODUCTS;
  const ORDER = window.DIALOGUE_ORDER;
  const BUNDLE = window.DIALOGUE_BUNDLE || {};

  /* ── tiny svg icon set (Lucide-ish, 1.5 stroke) ── */
  const I = {
    star: '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 3.2l2.5 5.6 6.1.5-4.6 4 1.4 6-5.4-3.2-5.4 3.2 1.4-6-4.6-4 6.1-.5z"/></svg>',
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>',
    minus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M5 12h14"/></svg>',
    bag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8h14l-1.2 11.3a2 2 0 0 1-2 1.7H8.2a2 2 0 0 1-2-1.7L5 8Z"/><path d="M8 8V6a4 4 0 0 1 8 0v2"/></svg>',
    menu: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M4 7h16"/><path d="M4 17h16"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
    leaf: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 4 13c0-5 4-9 16-9 0 9-4 13-9 13Z"/><path d="M4 20c3-6 7-8 11-9"/></svg>',
    rabbit: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M13 16a3 3 0 0 1 2.2 5"/><path d="M8 8c-2 0-3 1.5-3 4 0 3 2 5 6 5"/><path d="M8 8c0-3 1-5 2.5-5S13 5 13 8c0 1.6-.6 3-2 4"/><circle cx="6" cy="13" r=".5" fill="currentColor"/></svg>',
    truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h11v9H3z"/><path d="M14 9h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.6"/><circle cx="17.5" cy="18" r="1.6"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>',
    sparkle: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v6M12 15v6M3 12h6M15 12h6"/></svg>',
    shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z"/></svg>',
  };
  const stars = (n, cls) => `<span class="${cls}">${I.star.repeat(n)}</span>`;
  // ── market-aware money (Shopify Markets) ──
  // Active presentment currency + shop→active rate, from the Shopify global
  // that Shopify injects on every storefront page (matches homepage/cart).
  const activeCurrency = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
  const currencyRate = () => { const r = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate); return (r && isFinite(r)) ? r : 1; };
  // format a value given in the presentment currency's minor units (pence/cents)
  const fmtMoney = (minor, cur) => {
    cur = cur || activeCurrency();
    const v = (Number(minor) || 0) / 100;
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(v); }
    catch (e) { return '£' + v.toFixed(2); }
  };
  // Hardcoded data prices are GBP whole-pounds; convert to the active currency
  // via the market rate for the initial render. hydratePrices() then replaces
  // these with the exact presentment prices from Shopify once they load.
  const money = (pounds) => fmtMoney(Math.round((Number(pounds) || 0) * 100 * currencyRate()));

  /* request a sensibly-sized image off the Shopify CDN instead of the original */
  const sizedImg = (url, w) => !url ? url : url + (url.includes('?') ? '&' : '?') + 'width=' + w;

  /* escape admin-authored alt text so it can't break the gallery markup */
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  /* read a product-metafield image URL injected by Liquid into
     window.DIALOGUE_METAFIELDS (see the snippet in README/integration notes).
     Returns '' when unset so callers can fall back to the bundled asset. */
  const mf = (key) => {
    const m = window.DIALOGUE_METAFIELDS;
    return (m && m[key]) || '';
  };

  /* ── section builders ── */
  function gallery(p) {
    return `
    <div class="gal">
      <div class="gal-main arch">
        <img id="galMain" src="${p.gallery[0].src}" alt="${p.name}">
        <img class="glyph" src="${p.mark}" alt="">
        <div class="gal-cap mono" id="galCap">${p.gallery[0].label}</div>
        <button type="button" class="gal-nav gal-prev" id="galPrev" aria-label="Previous image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg></button>
        <button type="button" class="gal-nav gal-next" id="galNext" aria-label="Next image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg></button>
      </div>
      <div class="gal-thumbs-wrap">
        <button type="button" class="thumb-nav thumb-prev" id="thumbPrev" aria-label="Scroll thumbnails left"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg></button>
        <div class="gal-thumbs">
          ${p.gallery.map((g, i) => `
            <button class="gal-thumb" data-src="${g.src}" data-cap="${g.label}" aria-current="${i === 0}">
              <img src="${g.src}" alt="${g.label}">
            </button>`).join('')}
        </div>
        <button type="button" class="thumb-nav thumb-next" id="thumbNext" aria-label="Scroll thumbnails right"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg></button>
      </div>
    </div>`;
  }

  function buy(p) {
    return `
    <div class="buy">
      <div class="sign-row">
        <img src="${mf('glyph') || p.glyph}" alt="${p.sign} glyph">
        <span class="eyebrow">${p.eyebrow}</span>
      </div>
      <h1>${p.name}${p.name2 ? `<span class="l2">${p.name2}</span>` : ''}</h1>
      <div class="moods">${p.moods.join('<span class="s">//</span>')}</div>
      <p class="lede">${p.lede}</p>

      <div class="rating">
        ${stars(5, 'stars')}
        <span class="rcount">5.0 · <a href="#reviews">6 reviews</a></span>
      </div>

      <div class="proof">
        <div class="big serif">${p.proofStat}</div>
        <div class="ptxt">${p.proofClaim}<br><span class="mono" style="font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--fg-3)">28-day consumer study</span></div>
      </div>

      <div class="price-row">
        <span class="price">${money(p.price)}</span>
        <span class="size">${p.size}</span>
      </div>

      <div class="add-row">
        <div class="qty">
          <button id="qMinus" aria-label="Decrease quantity">${I.minus}</button>
          <span class="q" id="qVal">1</span>
          <button id="qPlus" aria-label="Increase quantity">${I.plus}</button>
        </div>
        <button class="btn btn-primary" id="addBtn">Add to bag · ${money(p.price)}</button>
      </div>
      <div class="assure">${p.naturalLine} · cruelty-free &amp; vegan</div>

      <div class="trust-row">
        <div class="ti"><div class="tt">${p.natPct}%</div><div class="ts">Natural origin</div></div>
        <div class="ti"><div class="tt">Vegan</div><div class="ts">Cruelty-free</div></div>
        <div class="ti"><div class="tt">Organic</div><div class="ts">Certified</div></div>
        <div class="ti"><div class="tt">${p.size}</div><div class="ts">${p.kind.split(' ').slice(-1)[0]}</div></div>
      </div>

      <div class="risk">
        <!--<div>${I.truck}<span>Free UK delivery over £60, dispatched within 48 hours</span></div>-->
        <div>${I.check}<span>30-day returns. Not right for your skin? Return within 30 days, no questions asked</span></div>
        <div>${I.sparkle}<span>Earn a place in the ritual, pairs with the full Celestial Three routine</span></div>
      </div>
    </div>`;
  }

  function marquee(p) {
    const words = p.moods.concat([p.sign, p.intention, 'In alignment']);
    const run = words.map(w => `<span>${w}</span><span class="s">//</span>`).join('');
    return `<div class="marq"><div class="marq-track">${run}${run}</div></div>`;
  }

  function results(p) {
    return `
    <section class="sec results rv">
      <div class="wrap">
        <div class="sec-head">
          <span class="eyebrow">Customer results</span>
          <h2>Proof you can <em class="ital">feel</em>.</h2>
        </div>
        <div class="res-grid">
          ${p.results.map(r => `
            <div class="res-cell">
              <div class="rp">${r.pct}</div>
              <div class="rt">${r.text}</div>
            </div>`).join('')}
        </div>
        <p class="res-note">${p.studyNote}</p>
      </div>
    </section>`;
  }

  function formula(p) {
    return `
    <section class="sec formula rv">
      <div class="wrap">
        <div class="sec-head">
          <span class="eyebrow">The formula</span>
          <h2>Three hero ingredients.</h2>
          <p>Shown openly and proudly, every active earns its place.</p>
        </div>
        <div class="ing-grid">
          ${p.heroIngredients.map(h => `
            <div class="ing-card">
              <div class="n serif">${h.n}</div>
              <h3>${h.name}</h3>
              <div class="tag">${h.tag}</div>
              <p>${h.text}</p>
            </div>`).join('')}
        </div>
        <p class="formula-line">${p.formulaLine}</p>
      </div>
    </section>`;
  }

  function energy(p) {
    return `
    <section class="energy rv">
      <div class="energy-in">
        <div class="energy-body">
          <span class="eyebrow">The energy · ${p.sign}</span>
          <h2 class="energy-words">${p.energyWords.map((w, i) => i === 1 ? `<em>${w}</em>` : w).join('<br>')}</h2>
          <p>${p.energyBody}</p>
          <div class="sign-table">
            ${[['Sign', p.sign], ['Element', p.element], ['Ruled by', p.ruler], ['Season', p.season], ['Skin intention', p.intention], ['Energy', p.energy]]
              .map(([k, v]) => `<div class="row"><span class="k">${k}</span><span class="v">${v}</span></div>`).join('')}
          </div>
        </div>
        <div class="energy-img">
          <img src="${mf('energy_image') || p.energyImage}" alt="${p.sign} energy">
          <div class="energy-quote">“${p.pullQuote}”</div>
        </div>
      </div>
    </section>`;
  }

  function who(p) {
    return `
    <section class="sec split rv">
      <div class="wrap split-in">
        <div class="who">
          <span class="eyebrow" style="display:block;margin-bottom:18px">Who it's for</span>
          <h2>Made for skin that needs</h2>
          <div class="lead-in">${p.forWho}.</div>
          <ul class="concerns">
            ${p.concerns.map(c => `<li>${I.check}<span>${c}</span></li>`).join('')}
          </ul>
        </div>
        <div class="profile-card">
          <div class="pc-h">Skin profile</div>
          ${p.skinProfile.map(([k, v]) => `<div class="prow"><span class="k">${k}</span><span class="v">${v}</span></div>`).join('')}
        </div>
      </div>
    </section>`;
  }

  function howto(p) {
    return `
    <section class="sec howto rv">
      <div class="wrap">
        <div class="sec-head">
          <span class="eyebrow">The ritual</span>
          <h2>How to use.</h2>
        </div>
        <div class="steps">
          ${p.steps.map(s => `
            <div class="step">
              <div class="n">STEP ${s.n}</div>
              <h4>${s.t}</h4>
              <p>${s.d}</p>
            </div>`).join('')}
        </div>
        <div class="upgrade">
          <img class="uglyph" src="${mf('ritual_upgrade_image') || p.mark}" alt="">
          <div>
            <div class="um">✦ ${p.id === 'leo' ? 'Ritual upgrade' : 'What to expect'}</div>
            <h4>${p.ritualUpgrade.title}</h4>
            <p>${p.ritualUpgrade.text}</p>
          </div>
        </div>
      </div>
    </section>`;
  }

  function bundle(p) {
    const total = ORDER.reduce((s, k) => s + P[k].price, 0); // £163
    const now = 150;
    const save = total - now; // £13
    return `
    <section class="sec bundle rv">
      <div class="wrap bundle-in">
        <div>
          <span class="eyebrow">The Celestial Three</span>
          <h2>The complete ritual.<br>Cleanse, renew, surrender.</h2>
          <p>Leo to melt the day away, Aquarius to renew, Pisces to seal it in. Three steps, three elements, designed to work as one. Bring the full routine home and save £13.</p>
          <div class="b-price">
            <span class="b-now">${money(now)}</span>
            <span class="b-was">${money(total)}</span>
            <span class="b-save">Save ${money(save)}</span>
          </div>
          <button class="btn btn-on" id="bundleAdd">Add the ritual to bag ${I.arrow}</button>
        </div>
        <div class="bundle-three">
          ${ORDER.map(k => {
            const x = P[k];
            return `<a class="bt" href="/products/${x.handle}">
              <div class="im"><img src="${mf('ritual_product_' + k) || x.gallery[0].src}" alt="${x.name}"></div>
              <div class="cap"><div class="st">Step ${x.step}</div><div class="nm">${x.name}</div></div>
            </a>`;
          }).join('')}
        </div>
      </div>
    </section>`;
  }

  function xsell(p) {
    const others = ORDER.filter(k => k !== p.id);
    return `
    <section class="sec xsell rv">
      <div class="wrap">
        <div class="sec-head">
          <span class="eyebrow">Complete the ritual</span>
          <h2>It works best in <em class="ital">three</em>.</h2>
        </div>
        <div class="xsell-grid">
          ${others.map((k, i) => {
            const x = P[k];
            return `<a class="xcard" href="#${k}" data-sign="${k}">
              <div class="xi arch"><img src="${mf('works_best_in_three_product_' + (i + 1)) || x.gallery[0].src}" alt="${x.name}"></div>
              <div>
                <div class="xstep">Step ${x.step} · ${x.sign}</div>
                <h3>${x.name}</h3>
                <div class="xp">${money(x.price)} · ${x.size}</div>
                <span class="xlink">View product ${I.arrow}</span>
              </div>
            </a>`;
          }).join('')}
        </div>
      </div>
    </section>`;
  }

  function reviews(p) {
    return `
    <section class="sec reviews rv" id="reviews">
      <div class="wrap">
        <div class="rev-top">
          <div class="rt-l">
            <span class="eyebrow">In their words</span>
            <h2>Loved by sensitive skin.</h2>
          </div>
          <div class="rev-sum">
            <div class="rs-stars">${I.star.repeat(5)}</div>
            <div class="rs-meta">5.0 average · based on 6 reviews</div>
          </div>
        </div>
        <div class="rev-grid">
          ${p.reviews.map(r => `
            <div class="rcard">
              <div class="rstars">${I.star.repeat(5)}</div>
              <h4>${r.title}</h4>
              <p>${r.body}</p>
              <div class="who-r">
                <div class="av">${r.name[0]}</div>
                <div><div class="rn">${r.name}</div><div class="rv">Verified buyer</div></div>
              </div>
            </div>`).join('')}
        </div>
      </div>
    </section>`;
  }

  function inci(p) {
    return `
    <section class="sec inci rv">
      <div class="wrap inci-in">
        <div class="sec-head" style="margin-bottom:40px">
          <span class="eyebrow">Full transparency</span>
          <h2>Every ingredient, in order of weight.</h2>
        </div>
        <div class="acc">
          <div class="acc-item open">
            <button class="acc-btn"><span class="at">Full ingredient list (INCI)</span>${I.plus}</button>
            <div class="acc-body"><div class="acc-inner">
              ${p.inci}
              <div class="free">Free from parabens, sulphates and mineral oil · Vegan · Cruelty-free · Contains natural fragrance. ${p.natPct}% ingredients of natural origin.</div>
            </div></div>
          </div>
          <div class="acc-item">
            <button class="acc-btn"><span class="at">How it's made</span>${I.plus}</button>
            <div class="acc-body"><div class="acc-inner">
              Blended in small batches in the EU with certified-organic, cold-pressed and renewable-sourced ingredients. ${p.formulaLine}
            </div></div>
          </div>
          <div class="acc-item">
            <button class="acc-btn"><span class="at">Shipping &amp; returns</span>${I.plus}</button>
            <div class="acc-body"><div class="acc-inner">
              Complimentary UK delivery on orders over £60, dispatched within 48 hours. Returns accepted within 30 days, if it isn't right for your skin, no questions asked.
            </div></div>
          </div>
        </div>
      </div>
    </section>`;
  }

  function why(p) {
    const cells = [
      { i: I.leaf, h: `${p.natPct}% Natural origin`, p: 'Certified-organic ingredients sourced from renewable EU farms.' },
      { i: I.rabbit, h: 'Cruelty-free & vegan', p: 'Never tested on animals. 100% vegan formula, always.' },
      { i: I.truck, h: 'Free UK delivery', p: 'On orders over £60, dispatched within 48 hours.' },
      { i: I.shield, h: '30-day returns', p: "Not right for your skin? Return within 30 days, no questions." },
    ];
    return `
    <section class="sec why rv">
      <div class="wrap">
        <div class="sec-head"><span class="eyebrow">Why Dialogue</span><h2>Clean science, meditative calm.</h2></div>
        <div class="why-grid">
          ${cells.map(c => `<div class="why-cell"><div class="wm">${c.i}</div><h4>${c.h}</h4><p>${c.p}</p></div>`).join('')}
        </div>
      </div>
    </section>`;
  }

  function stickyBar(p) {
    return `
    <div class="stick" id="stick">
      <div class="stick-in">
        <div class="sp-img"><img src="${p.gallery[0].src}" alt=""></div>
        <div>
          <div class="sp-name serif">${p.sign}</div>
          <div class="sp-meta">${p.kind} · ${p.size}</div>
        </div>
        <div class="sp-right">
          <span class="sp-price">${money(p.price)}</span>
          <button class="btn btn-primary" id="stickAdd">Add to bag</button>
        </div>
      </div>
    </div>`;
  }

  /* ── render a product ── */
  function render(id) {
    const p = P[id];
    document.body.setAttribute('data-sign', id);
    const app = document.getElementById('app');
    app.innerHTML = `
      <section class="hero">
        <div class="hero-in">
          ${gallery(p)}
          ${buy(p)}
        </div>
      </section>
      ${marquee(p)}
      ${results(p)}
      ${formula(p)}
      ${energy(p)}
      ${howto(p)}
      ${bundle(p)}
      ${reviews(p)}
      ${xsell(p)}
      ${inci(p)}
      ${why(p)}
    `;
    document.getElementById('stickMount').innerHTML = stickyBar(p);

    // nav active state
    document.querySelectorAll('[data-nav-sign]').forEach(el => {
      el.style.opacity = el.getAttribute('data-nav-sign') === id ? '1' : '';
      el.style.borderBottom = el.getAttribute('data-nav-sign') === id ? '1px solid currentColor' : '';
    });

    wire(p);
    hydrateGallery(p);
    hydrateLede(p);
    hydratePrices(p);
    window.scrollTo(0, 0);
    requestAnimationFrame(reveal);
  }

  /* ── interactions ── */
  // gallery thumb clicks — extracted so it can be re-bound after Shopify
  // media is loaded and the gallery markup is re-rendered.
  let galTimer = null; // auto-advance handle; cleared/reset on each (re)wire

  function wireGallery() {
    if (galTimer) { clearInterval(galTimer); galTimer = null; }

    const main = document.getElementById('galMain');
    const cap = document.getElementById('galCap');
    const thumbs = Array.from(document.querySelectorAll('.gal-thumb'));
    if (!main || !thumbs.length) return;

    let idx = thumbs.findIndex(t => t.getAttribute('aria-current') === 'true');
    if (idx < 0) idx = 0;

    // horizontally center a thumb within its own strip, WITHOUT scrolling the
    // page (scrollIntoView would pull the page up when the strip is off-screen)
    const centerThumb = (t) => {
      const strip = document.querySelector('.gal-thumbs');
      if (!strip) return;
      const delta = (t.getBoundingClientRect().left - strip.getBoundingClientRect().left)
        - (strip.clientWidth - t.clientWidth) / 2;
      strip.scrollBy({ left: delta, behavior: 'smooth' });
    };

    // show slide i (wraps around → loop)
    const show = (i) => {
      idx = (i + thumbs.length) % thumbs.length;
      const t = thumbs[idx];
      main.src = t.getAttribute('data-src');
      if (cap) cap.textContent = t.getAttribute('data-cap');
      thumbs.forEach(o => o.setAttribute('aria-current', 'false'));
      t.setAttribute('aria-current', 'true');
      centerThumb(t);
    };

    // auto-advance (loops); paused while hovering the main image
    const AUTO_MS = 4000;
    const start = () => { if (thumbs.length > 1 && !galTimer) galTimer = setInterval(() => show(idx + 1), AUTO_MS); };
    const stop = () => { if (galTimer) { clearInterval(galTimer); galTimer = null; } };
    const restart = () => { stop(); start(); };

    // thumbnail click → jump to that slide
    thumbs.forEach((t, i) => t.addEventListener('click', () => { show(i); restart(); }));

    // main image prev / next arrows
    const prevBtn = document.getElementById('galPrev');
    const nextBtn = document.getElementById('galNext');
    if (prevBtn) prevBtn.addEventListener('click', () => { show(idx - 1); restart(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { show(idx + 1); restart(); });

    // thumbnail strip scroll arrows
    const strip = document.querySelector('.gal-thumbs');
    const tPrev = document.getElementById('thumbPrev');
    const tNext = document.getElementById('thumbNext');
    const step = () => Math.max(120, Math.round((strip ? strip.clientWidth : 240) * 0.7));
    if (tPrev && strip) tPrev.addEventListener('click', () => strip.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (tNext && strip) tNext.addEventListener('click', () => strip.scrollBy({ left: step(), behavior: 'smooth' }));

    // hide the strip arrows when there's nothing to scroll
    const syncThumbNav = () => {
      if (!strip || !tPrev || !tNext) return;
      const overflow = strip.scrollWidth - strip.clientWidth > 2;
      tPrev.style.visibility = overflow ? '' : 'hidden';
      tNext.style.visibility = overflow ? '' : 'hidden';
    };
    syncThumbNav();
    if (window._dlgThumbSync) window.removeEventListener('resize', window._dlgThumbSync);
    window._dlgThumbSync = syncThumbNav;
    window.addEventListener('resize', syncThumbNav);

    // pause autoplay on hover, resume on leave
    const galMainEl = document.querySelector('.gal-main');
    if (galMainEl) {
      galMainEl.addEventListener('mouseenter', stop);
      galMainEl.addEventListener('mouseleave', start);
    }
    start();
  }

  /* ── Shopify storefront helpers ── */

  // The product handle from the current page URL, e.g. /products/test-bundle
  // (tolerates locale prefixes like /en-gb/products/...). '' if not a PDP URL.
  function urlProductHandle() {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  // Resolve the Shopify handle for a product: its explicit `handle` field, or
  // — for the product currently on screen — the handle in the page URL. This
  // keeps each product's own PDP working even if `handle` is left blank.
  function handleFor(p) {
    if (p.handle) return p.handle;
    if (document.body.getAttribute('data-sign') === p.id) return urlProductHandle();
    return '';
  }

  // Fetch + cache a product's JSON (/products/<handle>.js). Used for both the
  // gallery media and the variant id we add to cart. Returns null on failure
  // or when no handle can be resolved.
  async function fetchShopify(p) {
    const handle = handleFor(p);
    if (!handle) return null;
    if (p._shopify) return p._shopify;
    try {
      const res = await fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      p._shopify = await res.json();
      return p._shopify;
    } catch (e) {
      return null;
    }
  }

  // Pick the variant to add: first available, else the first one (so we can
  // still detect & message "sold out" rather than silently failing).
  function pickVariant(data) {
    if (!data || !data.variants || !data.variants.length) return null;
    return data.variants.find(v => v.available) || data.variants[0];
  }

  // Does a cart error read like an out-of-stock / inventory rejection?
  const isSoldOut = (msg) => /sold out|out of stock|not?.*enough|in your cart|unavailable|inventory/i.test(msg || '');

  // POST line items to the Shopify cart. items: [{ id, quantity }].
  async function cartAdd(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // Shopify returns { status, message, description } explaining the refusal.
      console.error('[Dialogue] /cart/add.js failed', res.status, 'sent:', items, 'response:', data);
      throw new Error(data.description || data.message || ('cart add failed: ' + res.status));
    }
    return data;
  }

  // Sync every cart counter we can reach + nudge the theme/drawer to refresh.
  async function updateBagCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      bag = cart.item_count;
      // our own nav badge + common live-theme header count selectors
      document.querySelectorAll('.bag-count, .cart-count, [data-cart-count], .cart-item-count, .header__cart-count, .site-header__cart-count')
        .forEach(el => { el.textContent = bag; });
      // let a cart drawer / theme refresh itself (selectors & events vary by theme)
      ['cart:refresh', 'cart:updated', 'cart:change', 'cart:build', 'ajaxCart.afterCartLoad']
        .forEach(name => document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { cart } })));
    } catch (e) { /* ignore */ }
  }

  /* ── pull gallery images + labels from Shopify product media ──
     Uses the storefront product JSON endpoint (/products/<handle>.js),
     which exposes each media item's image src and its alt text. The alt
     text becomes the slide label. Falls back silently to the hardcoded
     gallery if there's no handle, the fetch fails, or there are no images. */
  async function hydrateGallery(p) {
    try {
      const data = await fetchShopify(p);
      if (!data) return;
      const imgs = (data.media || [])
        .filter(m => m.media_type === 'image' && m.src)
        .map(m => ({ src: sizedImg(m.src, 1400), label: esc((m.alt || '').trim() || p.name) }));
      if (!imgs.length) return;
      // bail if the user switched products while we were fetching
      if (document.body.getAttribute('data-sign') !== p.id) return;
      p.gallery = imgs;
      const galEl = document.querySelector('.gal');
      if (galEl) {
        galEl.outerHTML = gallery(p);
        wireGallery();
      }
      const stickImg = document.querySelector('.sp-img img');
      if (stickImg) stickImg.src = imgs[0].src;
    } catch (e) {
      /* keep the hardcoded gallery */
    }
  }

  /* ── pull the intro copy (.lede) from the Shopify product description ──
     Uses the same cached /products/<handle>.js fetch; sets the .lede text to
     the product's description HTML. Falls back to the hardcoded lede when the
     description is empty or the fetch fails. */
  async function hydrateLede(p) {
    const data = await fetchShopify(p);
    if (!data) return;
    const html = (data.description || '').trim();
    if (!html) return;
    // bail if the user switched products while we were fetching
    if (document.body.getAttribute('data-sign') !== p.id) return;
    const el = document.querySelector('.lede');
    if (el) el.innerHTML = html;
  }

  /* ── market-aware prices ──
     /products/<handle>.js returns the exact presentment price in the active
     market currency, so we apply those to the buy box, add-to-bag button,
     sticky bar, bundle and cross-sell — matching what Shopify actually charges
     (the initial render only shows a rate-converted approximation). */
  async function hydratePrices(p) {
    const priceOf = (data) => { if (!data) return null; const v = pickVariant(data); return v ? v.price : data.price; };
    const [curD, leoD, aqD, pisD, bunD] = await Promise.all([
      fetchShopify(p), fetchShopify(P.leo), fetchShopify(P.aquarius), fetchShopify(P.pisces), fetchShopify(BUNDLE)
    ]);
    // bail if the user switched products while we were fetching
    if (document.body.getAttribute('data-sign') !== p.id) return;
    const cur = activeCurrency();
    const fmt = (minor) => fmtMoney(minor, cur);

    // current product → buy price, add-to-bag button, sticky bar
    const cp = priceOf(curD);
    if (cp != null) {
      const priceEl = document.querySelector('.buy .price');
      if (priceEl) priceEl.textContent = fmt(cp);
      const addBtn = document.getElementById('addBtn');
      if (addBtn && !addBtn.disabled) addBtn.textContent = 'Add to bag · ' + fmt(cp);
      const sp = document.querySelector('.sp-price');
      if (sp) sp.textContent = fmt(cp);
    }

    // bundle → now (bundle product), was (sum of the three), save (difference)
    const now = priceOf(bunD);
    const parts = [priceOf(leoD), priceOf(aqD), priceOf(pisD)];
    if (now != null && parts.every(x => x != null)) {
      const was = parts.reduce((s, x) => s + x, 0);
      const bn = document.querySelector('.b-now'); if (bn) bn.textContent = fmt(now);
      const bw = document.querySelector('.b-was'); if (bw) bw.textContent = fmt(was);
      const bs = document.querySelector('.b-save'); if (bs) bs.textContent = 'Save ' + fmt(Math.max(0, was - now));
    }

    // cross-sell cards → each other product's price
    document.querySelectorAll('.xcard[data-sign]').forEach(card => {
      const k = card.getAttribute('data-sign');
      const data = k === 'leo' ? leoD : k === 'aquarius' ? aqD : k === 'pisces' ? pisD : null;
      const pr = priceOf(data);
      const xp = card.querySelector('.xp');
      if (pr != null && xp) {
        const size = (P[k] && P[k].size) ? ' · ' + P[k].size : '';
        xp.textContent = fmt(pr) + size;
      }
    });
  }

  function wire(p) {
    wireGallery();

    // qty
    let q = 1;
    const qVal = document.getElementById('qVal');
    document.getElementById('qMinus').addEventListener('click', () => { q = Math.max(1, q - 1); qVal.textContent = q; });
    document.getElementById('qPlus').addEventListener('click', () => { q = q + 1; qVal.textContent = q; });

    // add to bag — real Shopify cart adds via /cart/add.js.
    // While a button is mid-add it's disabled and shows feedback; on success
    // we sync the header count, on failure we surface a retry message.
    const addBtn = document.getElementById('addBtn');

    // swap a button's label/state and return a fn that restores it
    const setBusy = (btn, label) => {
      const prev = btn.innerHTML;
      btn.innerHTML = label;
      btn.style.background = 'var(--accent)';
      btn.disabled = true;
      return () => { btn.innerHTML = prev; btn.style.background = ''; btn.disabled = false; };
    };

    // add a single variant, with button feedback. demoFallback runs when no
    // Shopify variant resolves (handle not set / standalone preview). Shows
    // "Sold out" when the variant is unavailable or the cart rejects on stock.
    async function addToBag(btn, variant, quantity, okLabel, demoFallback) {
      if (btn.disabled) return;
      if (!variant) { demoFallback(); return; }
      if (!variant.available) {
        const restore = setBusy(btn, 'Sold out');
        setTimeout(restore, 1800);
        return;
      }
      const restore = setBusy(btn, 'Adding…');
      try {
        await cartAdd([{ id: variant.id, quantity }]);
        // On success, send the customer to the cart page.
        btn.innerHTML = okLabel;
        window.location.href = '/cart';
      } catch (e) {
        btn.innerHTML = isSoldOut(e.message) ? 'Sold out' : 'Couldn’t add, try again';
        setTimeout(restore, 1800);
      }
    }

    // simple visual-only add (kept so the standalone preview still responds)
    const demoFlash = (btn, label, n) => {
      const restore = setBusy(btn, label);
      bagAdd(n);
      setTimeout(restore, 1500);
    };

    // current product → add `q` units
    const addCurrent = async (btn) => {
      const v = pickVariant(await fetchShopify(p));
      addToBag(btn, v, q, 'Added to bag ✦', () => demoFlash(btn, 'Added to bag ✦', q));
    };
    addBtn.addEventListener('click', () => addCurrent(addBtn));
    const stickAdd = document.getElementById('stickAdd');
    stickAdd.addEventListener('click', () => addCurrent(stickAdd));

    // the Celestial Three bundle → add the bundle product (DIALOGUE_BUNDLE)
    const bundleBtn = document.getElementById('bundleAdd');
    bundleBtn.addEventListener('click', async () => {
      const v = pickVariant(await fetchShopify(BUNDLE));
      addToBag(bundleBtn, v, 1, 'Ritual added ✦', () => demoFlash(bundleBtn, 'Ritual added ✦', 3));
    });

    // accordions
    document.querySelectorAll('.acc-item').forEach(item => {
      item.querySelector('.acc-btn').addEventListener('click', () => item.classList.toggle('open'));
    });

    // sticky bar on scroll past buy box
    const buyEl = document.querySelector('.buy');
    const stick = document.getElementById('stick');
    if (window._dlgObs) window._dlgObs.disconnect();
    window._dlgObs = new IntersectionObserver(([e]) => {
      stick.classList.toggle('on', !e.isIntersecting && e.boundingClientRect.top < 0);
    }, { threshold: 0 });
    window._dlgObs.observe(buyEl);

    // sign links (cards/bundle)
    document.querySelectorAll('[data-sign]').forEach(el => {
      el.addEventListener('click', (ev) => {
        if (el.tagName === 'A') { ev.preventDefault(); go(el.getAttribute('data-sign')); }
      });
    });
  }

  let bag = 0;
  function bagAdd(n) {
    bag += n;
    document.querySelectorAll('.bag-count').forEach(el => el.textContent = bag);
  }

  /* ── reveal on scroll ── */
  let revObs;
  function reveal() {
    if (revObs) revObs.disconnect();
    revObs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); revObs.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('.rv').forEach(el => revObs.observe(el));
  }

  /* ── routing ── */
  function go(id) {
    if (!P[id]) id = 'leo';
    if (history.replaceState) history.replaceState(null, '', '#' + id);
    else location.hash = id;
    render(id);
  }
  window.addEventListener('hashchange', () => {
    const id = location.hash.replace('#', '');
    if (P[id] && document.body.getAttribute('data-sign') !== id) render(id);
  });

  // expose for nav
  window.DLG_GO = go;

  // init
  const start = location.hash.replace('#', '');
  render(P[start] ? start : 'leo');
  updateBagCount(); // reflect any existing Shopify cart in the header
})();
